import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import type { HardwareDevice } from '../../api/types';
import HardwareVendorMark from './HardwareVendorMark';

/// libav* names a codec the way its implementers do. People know them the way the standards
/// bodies and the marketing did, so that is what the page shows; anything not in here is
/// shown as it came, rather than guessed at.
const codecNames: Record<string, string> = {
  h264: 'H.264',
  hevc: 'HEVC',
  av1: 'AV1',
  vp8: 'VP8',
  vp9: 'VP9',
  mpeg1video: 'MPEG-1',
  mpeg2video: 'MPEG-2',
  mpeg4: 'MPEG-4',
  vc1: 'VC-1',
  h263: 'H.263',
  h263p: 'H.263+',
  prores: 'ProRes',
  vvc: 'VVC',
};

function codecName(codec: string): string {
  return codecNames[codec.toLowerCase()] ?? codec.toUpperCase();
}

/// One GPU, with what it can do and whether it is being used.
///
/// A device that is switched off keeps its whole matrix on the page. Hiding it would leave
/// somebody looking for the card they know is in the machine, and the reason it is not being
/// used is the one thing the page has to make obvious.
export default function HardwareDeviceCard({
  device,
  onToggle,
  busy,
}: {
  device: HardwareDevice;
  onToggle: (enabled: boolean) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const off = !device.isEnabled;

  // 10 bit is what carries HDR through a transcode instead of flattening it.
  const decodes = device.decoders.map((decoder) => codecName(decoder.codec));

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: off ? 'divider' : 'primary.main',
        transition: 'border-color 160ms ease',
      }}
    >
      <CardContent sx={{ opacity: off ? 0.6 : 1, transition: 'opacity 160ms ease' }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
          <HardwareVendorMark accelerator={device.accelerator} dimmed={off} />

          <Stack spacing={0.75} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
              <Typography variant="h6" sx={{ minWidth: 0 }}>
                {device.name}
              </Typography>

              {off && <Chip size="small" color="default" label={t('hardware.switchedOff')} />}
            </Stack>

            {/* The driver says which version is doing the work, which is the first thing
                anybody asks when a codec is missing that the card should have. */}
            {device.driverVersion && (
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {device.driverVersion}
              </Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
              <Chip size="small" variant="outlined" label={device.accelerator} />

              {device.supportsHardwareDeinterlace && (
                <Chip size="small" variant="outlined" label={t('dashboard.deinterlace')} />
              )}

              {/* Which tone mapper, because it says which path the picture takes: Intel's video
                  processor, libplacebo on Vulkan, or Apple's pixel transfer. */}
              {device.toneMapper && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('hardware.toneMapping', { toneMapper: device.toneMapper })}
                />
              )}

              {device.maxConcurrentEncodeSessions !== null && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('hardware.sessionLimit', { count: device.maxConcurrentEncodeSessions })}
                />
              )}

              {device.devicePath && (
                <Chip size="small" variant="outlined" label={device.devicePath} />
              )}
            </Stack>
          </Stack>

          <Tooltip title={t(device.isEnabled ? 'hardware.turnOff' : 'hardware.turnOn')}>
            <Box component="span">
              <Switch
                checked={device.isEnabled}
                disabled={busy}
                onChange={(event) => onToggle(event.target.checked)}
                slotProps={{ input: { 'aria-label': t('hardware.useDevice', { name: device.name }) } }}
              />
            </Box>
          </Tooltip>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              {t('dashboard.encoders')}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 0.5 }} useFlexGap>
              {device.encoders.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('hardware.noEncoders')}
                </Typography>
              )}

              {device.encoders.map((encoder) => (
                <Chip
                  key={encoder.encoderName}
                  size="small"
                  color={off ? 'default' : 'primary'}
                  variant={off ? 'outlined' : 'filled'}
                  label={
                    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'baseline' }}>
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {codecName(encoder.codec)}
                      </Box>
                      <Box component="span" sx={{ opacity: 0.75, fontSize: 11 }}>
                        {t('hardware.bitDepth', { depth: encoder.maxBitDepth })}
                      </Box>
                      {encoder.supportedRanges.some((range) => range !== 'Sdr') && (
                        <Box component="span" sx={{ opacity: 0.75, fontSize: 11 }}>
                          HDR
                        </Box>
                      )}
                    </Stack>
                  }
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">
              {t('hardware.decoders')}
            </Typography>

            {/* A list rather than chips: nobody picks a decoder, they only want to know
                whether the file they have is going to be read by the card or by the CPU. */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {decodes.length > 0 ? decodes.join(' · ') : t('hardware.noDecoders')}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
